/**
 * Which strings in a site are missing a language, and how to put one back.
 *
 * ⚠️ THIS EXISTS BECAUSE A SITE IS NO LONGER ALWAYS WRITTEN IN BOTH LANGUAGES AT ONCE. Generation
 * used to demand `en` and `ar` from the model for every field, and the second half of that bill was
 * paid by everybody — including the visitor who is going to look at four screens in the language
 * they arrived in and close the tab. So a site built by somebody with no account is written in ONE
 * language and the other is filled in when it is actually wanted: when they switch language, or when
 * they publish. See CLAUDE.md §5.15.
 *
 * ⚠️ IT IS A LIST OF PLACES AND A WAY TO WRITE ONE BACK — NEVER A TRANSLATOR. Nothing here calls
 * anything. The browser collects the gaps, asks the API for the strings, and writes each one back
 * through `applyGap`, which is `translated()` on the leaf and nothing else. That is the same
 * boundary `/ai/translate` has held since it existed (CLAUDE.md §5.10): the model is handed strings
 * and hands back strings, and cannot move a block, follow a link or touch a field nobody asked
 * about.
 *
 * ⚠️ AND IT IS DRIVEN BY THE REGISTRY, like everything else that walks a document (§0.5). The labels
 * it reports are the field labels the editor draws — which is exactly the disambiguation the
 * translator wants, because «Menu» is `قائمة الطعام` on a restaurant page and `القائمة` in a navbar.
 */

import { eachText, type TextAt } from './copy.ts'
import { setPageTitle, setProp, setSiteTitle } from './ops.ts'
import { isLit, isText, translated, type Lang } from './text.ts'
import type { Site } from './types.ts'

/**
 * Where a gap is.
 *
 * ⚠️ IT IS AN ALIAS RATHER THAN A TYPE OF ITS OWN, because a gap's address is just a string's
 * address — see `TextAt`, which moved to `copy.ts` with the walk that produces it. The name survives
 * so `applyGap(site, gap.at, …)` still reads as itself at every call site.
 */
export type GapAt = TextAt

export type Gap = {
  at: GapAt
  /** What the field is called on screen, in the language the person is working in. Context, not instruction. */
  label: string
  /** The copy that exists, in the language it exists in. */
  source: string
}

/**
 * Every string that has one language and not the other.
 *
 * ⚠️ IT REPORTS ONLY WHAT IS **MISSING**, NEVER WHAT IS MERELY **BEHIND**. `stale` (§2.5) means the
 * customer edited one side and the other has not caught up — a judgement they may well have made on
 * purpose, and the drawer already offers to fix each one where they can see it. Sweeping those up in
 * a bulk pass would silently overwrite copy somebody wrote, which is a different act from filling in
 * a blank.
 *
 * ⚠️ AND `{ lit }` IS NOT A GAP. One string used in both languages is a phone number, a price, a
 * brand — the values that must not be translated at all.
 */
export function textGaps(site: Site, to: Lang, labelLang: Lang): Gap[] {
  const from: Lang = to === 'en' ? 'ar' : 'en'
  const gaps: Gap[] = []

  eachText(site, labelLang, ({ at, label, value }) => {
    if (isLit(value)) return
    if (value[to]?.trim()) return
    const source = value[from]?.trim()
    if (!source) return
    gaps.push({ at, label, source })
  })

  return gaps
}

/**
 * Write one translation back.
 *
 * ⚠️ IT IS `translated()` AND NOT `edited()`, WHICH IS THE WHOLE DIFFERENCE. Filling in a blank is a
 * language CATCHING UP; `edited()` would mark the source as behind, so translating a site would
 * leave every field in it stale in the other direction — the exact non-convergence §2.5's asymmetry
 * exists to prevent, arrived at from the other end.
 *
 * ⚠️ AND A GAP THAT NO LONGER EXISTS IS A NO-OP RATHER THAN AN ERROR. The document moves while the
 * request is in flight: a block gets deleted, a row gets removed, the customer types the Arabic
 * themselves. Returning the site unchanged is the honest outcome of "put this string there" when
 * there is no longer a there.
 */
export function applyGap(site: Site, at: GapAt, lang: Lang, text: string): Site {
  if (!text.trim()) return site

  if (at.where === 'site') return setSiteTitle(site, translated(site.title, lang, text))

  if (at.where === 'page') {
    const page = site.pages.find((p) => p.id === at.pageId)
    if (!page) return site
    return setPageTitle(site, page.id, translated(page.title, lang, text))
  }

  const ref = at.ref
  const block =
    ref.scope === 'navbar'
      ? site.navbar
      : ref.scope === 'footer'
        ? site.footer
        : site.pages.find((p) => p.id === ref.pageId)?.blocks.find((b) => b.id === ref.blockId)
  if (!block) return site

  if (at.index === undefined || !at.subKey) {
    const value = block.props[at.key]
    if (!isText(value)) return site
    return setProp(site, at.ref, at.key, translated(value, lang, text))
  }

  const rows = block.props[at.key]
  if (!Array.isArray(rows)) return site
  const row = rows[at.index] as Record<string, unknown> | undefined
  if (!row || typeof row !== 'object') return site
  const value = row[at.subKey]
  if (!isText(value)) return site

  const next = rows.map((item, i) =>
    i === at.index ? { ...(item as Record<string, unknown>), [at.subKey!]: translated(value, lang, text) } : item,
  )
  return setProp(site, at.ref, at.key, next)
}

/**
 * How many strings are missing this language.
 *
 * ⚠️ A COUNT RATHER THAN A BOOLEAN, because the number is what goes on screen. "This site is not
 * finished in English" is a sentence somebody argues with; "12 things are still only in Arabic" is
 * one they can act on, and it is the same number the progress bar counts down.
 */
export function gapCount(site: Site, to: Lang): number {
  return textGaps(site, to, to).length
}
